"use client"

import { useCallback, useMemo, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import {
  usePatchCandidatesCandidateId,
  usePatchCandidatesCandidateIdExperiences,
} from "openapi/api/endpoints"

export type EditableExperience = {
  /** Absent on an experience the API cannot address; it is then read-only. */
  experienceId?: string
  title: string
  company: string
  startsAt: string
  endsAt: string
  description: string
}

export type ProfileDraft = {
  fullName: string
  headline: string
  summary: string
  location: string
  tags: string[]
  experiences: EditableExperience[]
}

/** One line of canvas 14e's "Changed" rail: what moved, from what, to what. */
export type ProfileChange = {
  field: string
  from: string
  to: string
}

const text = (value: unknown) => (typeof value === "string" ? value : "")

/** `starts_at` arrives as an ISO date, a plain day, or a {year, month} object. */
function toDayInput(value: unknown): string {
  if (!value) return ""
  if (typeof value === "string") {
    const day = /^(\d{4}-\d{2}-\d{2})/.exec(value)
    return day ? day[1] : ""
  }
  if (typeof value === "object") {
    const parts = value as { year?: number; month?: number; day?: number }
    if (!parts.year) return ""
    const month = String(parts.month ?? 1).padStart(2, "0")
    const day = String(parts.day ?? 1).padStart(2, "0")
    return `${parts.year}-${month}-${day}`
  }
  return ""
}

function readDraft(profile: Record<string, unknown>): ProfileDraft {
  const raw = (profile.raw_json as Record<string, unknown>) ?? {}
  const pick = (key: string) => profile[key] ?? raw[key]

  const experiences = (pick("experiences") as Record<string, unknown>[] | undefined) ?? []
  /*
    Tags only — never fall back to `skills`. Save writes this array back as
    `tags`, so seeding it from skills would quietly convert a candidate's parsed
    skills into organisation tags the moment any unrelated field was edited.
  */
  const tags = pick("tags") as string[] | undefined

  return {
    fullName: text(pick("full_name")),
    headline: text(pick("headline")),
    summary: text(pick("summary")),
    location: text(pick("location")) || text(pick("location_str")),
    tags: Array.isArray(tags) ? tags.filter(Boolean) : [],
    experiences: experiences.map((experience) => ({
      experienceId:
        (experience.candidate_experience_id as string | undefined) ??
        (experience.id as string | undefined),
      title: text(experience.title),
      company: text(experience.company),
      startsAt: toDayInput(experience.starts_at),
      endsAt: toDayInput(experience.ends_at),
      description: text(experience.description),
    })),
  }
}

const blankToNull = (value: string) => {
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

/**
 * The candidate profile edit session: the draft, what has changed, and the save.
 *
 * The record is the source of truth for the initial values, so the draft is
 * seeded from it and the diff is computed here rather than in the UI — canvas
 * 14e's rail is "what changed, from what, to what", which is derivation, not
 * presentation.
 */
export function useCandidateProfileEdit({
  candidateId,
  jobId,
  profile,
}: {
  candidateId: string
  jobId?: string | null
  profile: Record<string, unknown>
}) {
  const initial = useMemo(() => readDraft(profile), [profile])

  const [draft, setDraft] = useState<ProfileDraft>(initial)
  /*
    Two reasons to reseed, and they are different.

    A different candidate: the overlay is one long-lived component, so opening a
    second record does not remount this hook, and without this the second
    candidate would open showing the first one's draft.

    Fresher data for the *same* candidate: react-query renders cached values
    first and then refetches, so `initial` can change under an open form. While
    the draft is untouched, follow it — otherwise the form would show a diff the
    user never made, and saving would push the stale values back. Once anything
    has been typed the draft wins, so a background refetch never eats an edit.
  */
  const [seededFor, setSeededFor] = useState(candidateId)
  const [touched, setTouched] = useState(false)
  const initialKey = JSON.stringify(initial)
  const [seededKey, setSeededKey] = useState(initialKey)

  if (seededFor !== candidateId) {
    setSeededFor(candidateId)
    setSeededKey(initialKey)
    setTouched(false)
    setDraft(initial)
  } else if (!touched && seededKey !== initialKey) {
    setSeededKey(initialKey)
    setDraft(initial)
  }

  const queryClient = useQueryClient()
  const { mutateAsync: patchProfile, isPending: isSavingProfile } =
    usePatchCandidatesCandidateId()
  const { mutateAsync: patchExperience, isPending: isSavingExperience } =
    usePatchCandidatesCandidateIdExperiences()

  const set = useCallback(<K extends keyof ProfileDraft>(key: K, value: ProfileDraft[K]) => {
    setTouched(true)
    setDraft((current) => ({ ...current, [key]: value }))
  }, [])

  const setExperience = useCallback(
    (index: number, patch: Partial<EditableExperience>) => {
      setTouched(true)
      setDraft((current) => ({
        ...current,
        experiences: current.experiences.map((experience, position) =>
          position === index ? { ...experience, ...patch } : experience,
        ),
      }))
    },
    [],
  )

  const changes = useMemo<ProfileChange[]>(() => {
    const list: ProfileChange[] = []
    const compare = (field: string, from: string, to: string) => {
      if (from.trim() !== to.trim()) list.push({ field, from: from || "—", to: to || "—" })
    }

    compare("Full name", initial.fullName, draft.fullName)
    compare("Headline", initial.headline, draft.headline)
    compare("Location", initial.location, draft.location)
    compare("Summary", initial.summary, draft.summary)

    if (initial.tags.join("|") !== draft.tags.join("|")) {
      const added = draft.tags.filter((tag) => !initial.tags.includes(tag))
      const removed = initial.tags.filter((tag) => !draft.tags.includes(tag))
      const detail = [
        added.length ? `added ${added.join(", ")}` : "",
        removed.length ? `removed ${removed.join(", ")}` : "",
      ]
        .filter(Boolean)
        .join(" · ")
      list.push({
        field: "Tags",
        from: `${initial.tags.length} tags`,
        to: `${draft.tags.length}${detail ? ` · ${detail}` : ""}`,
      })
    }

    draft.experiences.forEach((experience, index) => {
      const before = initial.experiences[index]
      // An experience with no id cannot be patched, so it is read-only in the
      // form and must not count as a change — otherwise Save would light up for
      // an edit that silently could not be sent.
      if (!before || !experience.experienceId) return
      const label = experience.title || `Experience ${index + 1}`
      compare(`${label} · title`, before.title, experience.title)
      compare(`${label} · company`, before.company, experience.company)
      compare(`${label} · since`, before.startsAt, experience.startsAt)
      compare(`${label} · until`, before.endsAt, experience.endsAt)
      compare(`${label} · description`, before.description, experience.description)
    })

    return list
  }, [initial, draft])

  const save = useCallback(async () => {
    if (!candidateId) return false

    try {
      await patchProfile({
        candidateId,
        data: {
          full_name: blankToNull(draft.fullName),
          headline: blankToNull(draft.headline),
          summary: blankToNull(draft.summary),
          location: blankToNull(draft.location),
          tags: draft.tags.length > 0 ? draft.tags : null,
        },
      })

      // Experiences are patched one at a time, and only the ones that moved.
      for (const [index, experience] of draft.experiences.entries()) {
        const before = initial.experiences[index]
        const unchanged =
          before &&
          before.title === experience.title &&
          before.company === experience.company &&
          before.description === experience.description &&
          before.startsAt === experience.startsAt &&
          before.endsAt === experience.endsAt
        if (!experience.experienceId || unchanged) continue

        await patchExperience({
          candidateId,
          data: {
            candidate_experience_id: experience.experienceId,
            title: blankToNull(experience.title),
            company: blankToNull(experience.company),
            starts_at: blankToNull(experience.startsAt),
            ends_at: blankToNull(experience.endsAt),
            description: blankToNull(experience.description),
          },
        })
      }

      await queryClient.invalidateQueries({ queryKey: ["/candidates"], exact: false })
      await queryClient.invalidateQueries({
        queryKey: [`/candidates/${candidateId}`],
        exact: false,
      })
      if (jobId) {
        await queryClient.invalidateQueries({
          queryKey: [`/jobs/${jobId}/candidate`],
          exact: false,
        })
        await queryClient.invalidateQueries({
          queryKey: [`/jobs/${jobId}/pipeline`],
          exact: false,
        })
      }

      setTouched(false)
      toast.success("Profile updated")
      return true
    } catch (error) {
      toast.error("Could not save the profile", { description: `${error}` })
      return false
    }
  }, [candidateId, draft, initial, jobId, patchExperience, patchProfile, queryClient])

  const discard = useCallback(() => {
    setTouched(false)
    setDraft(initial)
  }, [initial])

  return {
    draft,
    set,
    setExperience,
    changes,
    isDirty: changes.length > 0,
    save,
    discard,
    isSaving: isSavingProfile || isSavingExperience,
  }
}
